/* eslint-disable max-lines */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import shallow from 'zustand/shallow';
import native from '@native';
import { IPackage, IPackagePrice } from '@root/types/package';
import Modal from '@root/components/modal';
import { useDefaultPackageState, useUnBindDevicesState } from '@root/state';
import { packageApi } from '@root/api';
import useCreateOrder from '../newSubscription/useCreateOrder';
import useApplePayCreateOrder from '../applePay/useCreateOrder';
import useBillAddress from '../useBillAddress';
import useUnBindDevices from '../useUnBindDevices';
import { EPayMode, UNLIMITED_PACKAGE_NUMBER } from '@root/constant';
import { getQueryString, isInApp } from '@root/shared';
import { useCurrentHouseId } from '../useHouseList';
import { loggerOperatorOnTrack } from '@root/logger/loggerOperator';

interface IProps {
  payMode: EPayMode;
}

export const TAB_SINGLE = 0;
export const TAB_UNLIMITED = 1;

const useDefaultPackage = ({ payMode }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resetUnBindUpdateTime = useUnBindDevicesState(
    (state) => state.resetUpdateTime,
  );
  const { list: unBindDevices } = useUnBindDevices();
  const [loading, setLoading] = useState(0); // 0, 1, 2 loading， success，fail
  const {
    curPackage,
    updateTime,
    selectedPrice,
    subscribedInfoWithUnit,
    statePayMode,
    setPackage,
    setSelectedPrice,
    setSubscribedInfo,
    resetInitData,
  } = useDefaultPackageState(
    (state) => ({
      curPackage: state.packageData,
      selectedPrice: state.selectedPrice,
      subscribedInfoWithUnit: state.subscribedInfo,
      updateTime: state.updateTime,
      statePayMode: state.payMode,
      setPackage: state.setPackage,
      setSelectedPrice: state.setSelectedPrice,
      setSubscribedInfo: state.updateSubscribedInfo,
      resetInitData: state.resetInitData,
    }),
    shallow,
  );
  const { paying } = useCreateOrder();
  const { paying: applePaying, onCreateOrder: onApplePayCreateOrder } =
    useApplePayCreateOrder();
  const { isBillAddress, onAddBill } = useBillAddress();
  const [checkedTerm, setCheckedTerm] = useState(true);
  const initRef = useRef(true);
  const checkedPriceIdRef = useRef(selectedPrice.goodsPriceId);
  const navigateType = useNavigationType();
  const houseId = useCurrentHouseId();

  const [tab, setTab] = useState(TAB_SINGLE);

  const sensorOriginal = getQueryString('sensor') || '';

  // 根据选中的套餐是年还是月获取对应的
  const subscribedInfo = useMemo(() => {
    const _info = subscribedInfoWithUnit[selectedPrice.planUnit] || {};
    return {
      ..._info,
      loaded: subscribedInfoWithUnit.loaded,
      unlimitedPackageId:
        _info.unlimitedPackageId || subscribedInfoWithUnit.unlimitedPackageId,
    };
  }, [selectedPrice.planUnit, subscribedInfoWithUnit]);

  /**
   * 0 buyNum = 1 单台设备套餐
   * 1 buyNum = UNLIMITED_PACKAGE_NUMBER 无限套餐
   */
  const defaultPriceType = getQueryString('defaultPriceType');
  const defaultUnit = getQueryString('unit') || 'month';

  const getDefaultSelectedPrice = useCallback(
    (data: IPackagePrice[]) => {
      const _selectedPrice =
        data.find(
          (i: IPackagePrice) => i.planUnit === defaultUnit && i.buyNum === 1,
        ) || data[0];

      // 默认选中无限套餐价格
      if (defaultPriceType === '1') {
        setTab(TAB_UNLIMITED);
        return (
          data.find(
            (i: IPackagePrice) =>
              i.planUnit === defaultUnit &&
              i.buyNum === UNLIMITED_PACKAGE_NUMBER,
          ) || _selectedPrice
        );
      }

      return _selectedPrice;
    },
    [defaultPriceType, defaultUnit],
  );

  const updateSubscribedInfo = useCallback(() => {
    // 查询当前账号下套餐的绑定情况
    // 主要涉及 Apple Pay的升降级以及已经有无限设备的提示
    packageApi
      .getSubscribedInfo({ payMode, level: 15, houseId })
      .then((res: any) => {
        const _data = res || [];

        // 只要有无限设备套餐就要提示用户
        let _unlimitedPackageId = '';

        const _subscribedInfo = _data.reduce((all: any, item: any) => {
          const {
            devList,
            isApplePayUnlimited,
            unlimitPackageId,
            applepayHouseId,
            ...rest
          } = item;
          _unlimitedPackageId = unlimitPackageId || _unlimitedPackageId;
          // 开通了年订阅，年订阅是另外一个Apple Pay的套餐组
          // 所以套餐是否可以购买或升降级判断需要根据年或者月来进行
          all[item.planUnit] = {
            hasApplePayUnlimited: isApplePayUnlimited,
            deviceList: devList || [],
            unlimitedPackageId: unlimitPackageId,
            otherHouseHasApplePay:
              applepayHouseId && applepayHouseId !== houseId,
            ...rest,
          };

          return all;
        }, {});

        // devList apple pay 已经绑定的设备列表，选择设备时要包括这个
        // 如果年订阅 apple 套餐是分组的，那就需要再加上Apple Pay 年订阅绑定的设备，同样在设备时也要包括这些
        setSubscribedInfo({
          loaded: 1,
          unlimitedPackageId: _unlimitedPackageId,
          ..._subscribedInfo,
        });
      })
      .catch((e) => {
        setSubscribedInfo({
          loaded: 2,
        });
      });
  }, [houseId, payMode, setSubscribedInfo]);

  const getPackageData = useCallback(() => {
    setLoading(0);
    packageApi
      .getPackages({ level: 15, pageSize: 1, payMode })
      .then((data: any) => {
        const _data = data?.list?.[0] || {};
        setPackage(_data as IPackage, payMode);
        setLoading(1);

        if (_data.goodsPriceList?.length) {
          // 本地是否有选中的
          if (
            checkedPriceIdRef.current &&
            _data.goodsPriceList.find(
              (i: IPackagePrice) =>
                i.goodsPriceId === checkedPriceIdRef.current,
            )
          ) {
            return;
          }

          setSelectedPrice(getDefaultSelectedPrice(_data.goodsPriceList));
        }
      })
      .catch(() => {
        setLoading(2);
      });
  }, [getDefaultSelectedPrice, payMode, setPackage, setSelectedPrice]);

  const onSelectPrice = useCallback(
    (packagePrice: IPackagePrice) => () => {
      setSelectedPrice(packagePrice);
    },
    [setSelectedPrice],
  );

  const onChangeTerm = useCallback((e: any) => {
    setCheckedTerm(e.target.checked);
  }, []);

  const onClickTerm = useCallback(() => {
    setCheckedTerm((v) => !v);
  }, []);

  const onClickSupplemental = useCallback(() => {
    if (isInApp && window.WebViewJavascriptBridge) {
      native.system.openBrowser(
        'https://www.aidot.com/page/supplemental-terms',
      );
    } else {
      window.open('https://www.aidot.com/page/supplemental-terms', '_blank');
    }
  }, []);

  const onClickAppleAgreement = useCallback(() => {
    if (isInApp && window.WebViewJavascriptBridge) {
      native.system.openBrowser(
        'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/',
      );
    } else {
      window.open(
        'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/',
        '_blank',
      );
    }
  }, []);

  const isUnBind = unBindDevices.length > 0;

  // 神策数据上报
  const sensorTrack = useCallback(
    (params = {}) => {
      if (!sensorOriginal) {
        return;
      }
      loggerOperatorOnTrack('CloudServiceClickSubscribe', {
        price: `${selectedPrice.price}`,
        original: sensorOriginal,
        ...params,
      });
    },
    [selectedPrice, sensorOriginal],
  );

  const continueApplePayOrder = useCallback(() => {
    sensorTrack();

    // 有未绑定的设备 或 当前选中的非无限套餐并且已经订阅过 Apple Pay
    // 因为Apple Pay 同个组的套餐只能订阅第一个，只要有订阅过 Apple Pay，必须跳转选择设备，
    if (
      isUnBind ||
      (subscribedInfo.maxDevNum &&
        selectedPrice.buyNum !== UNLIMITED_PACKAGE_NUMBER)
    ) {
      navigate(`/applePay/selectDevice?sensor=${sensorOriginal}`);
      return;
    }

    // 没有可绑定的设备 Apple Pay 直接拉起支付

    // 升级套餐需要把已绑定在 Apple Pay 套餐的设备回传
    const subscribedIds = subscribedInfo.deviceList?.map((i) => i.id) || [];

    onApplePayCreateOrder({
      packageInfo: curPackage,
      priceInfo: selectedPrice,
      deviceIds: subscribedIds,
    });
  }, [
    curPackage,
    isUnBind,
    navigate,
    onApplePayCreateOrder,
    selectedPrice,
    sensorOriginal,
    sensorTrack,
    subscribedInfo.deviceList,
    subscribedInfo.maxDevNum,
  ]);

  const continueApplePay = useCallback(() => {
    if (subscribedInfo.otherHouseHasApplePay) {
      Modal.alert({
        content: t('other_house_has_apple_pay_tips'),
        confirm: t('ok'),
      });
      return;
    }

    if (subscribedInfo.unlimitedPackageId) {
      Modal.confirm({
        content: t('has_unlimited_service_tips'),
        cancel: t('Cancel'),
        confirm: t('check_unlimited_service'),
        actions: subscribedInfo.hasApplePayUnlimited
          ? null
          : [t('continue_new_service')],
        footerColumn: true,
        onConfirm: () => {
          // 去套餐详情页
          navigate(`/subscription/${subscribedInfo.unlimitedPackageId}`);
        },
        onActions: () => {
          continueApplePayOrder();
        },
      });
      return;
    }

    continueApplePayOrder();
  }, [subscribedInfo, continueApplePayOrder, t, navigate]);

  const continuePayPal = useCallback(() => {
    // PayPal 需要计算税费
    if (!isBillAddress) {
      onAddBill();
      return;
    }

    if (subscribedInfo.unlimitedPackageId) {
      Modal.confirm({
        content: t('has_unlimited_service_tips'),
        cancel: t('Cancel'),
        confirm: t('check_unlimited_service'),
        actions: [t('continue_new_service')],
        footerColumn: true,
        onConfirm: () => {
          // 去套餐详情页
          navigate(`/subscription/${subscribedInfo.unlimitedPackageId}`);
        },
        onActions: () => {
          sensorTrack();
          navigate(`/defaultPackage/selectDevice?sensor=${sensorOriginal}`);
          // if (isUnBind) {
          //   navigate(`/defaultPackage/selectDevice?sensor=${sensorOriginal}`);
          // } else {
          //   onCreateOrder({
          //     packageInfo: curPackage,
          //     priceInfo: selectedPrice,
          //   });
          // }
        },
      });
      return;
    }

    sensorTrack();
    navigate(`/defaultPackage/selectDevice?sensor=${sensorOriginal}`);

    // if (isUnBind) {
    //   navigate(`/defaultPackage/selectDevice?sensor=${sensorOriginal}`);
    // } else {
    //   onCreateOrder({ packageInfo: curPackage, priceInfo: selectedPrice });
    // }
  }, [
    isBillAddress,
    sensorTrack,
    subscribedInfo.unlimitedPackageId,
    navigate,
    sensorOriginal,
    onAddBill,
    t,
  ]);

  const onClickPay = useCallback(() => {
    // 隐私模式
    if (!checkedTerm) {
      Modal.alert({
        title: '',
        content: t('apple_pay_plan_terms'),
        confirm: t('ok'),
      });
      return;
    }

    if (payMode === EPayMode.ApplePay) {
      continueApplePay();
    } else {
      continuePayPal();
    }
  }, [checkedTerm, payMode, continuePayPal, t, continueApplePay]);

  const fetchPackageData = useCallback(() => {
    getPackageData();
    updateSubscribedInfo();
  }, [getPackageData, updateSubscribedInfo]);

  const onChangeTab = useCallback((v: number) => {
    setTab(v);
  }, []);

  useEffect(() => {
    resetUnBindUpdateTime();
  }, [resetUnBindUpdateTime]);

  useEffect(() => {
    updateSubscribedInfo();
  }, [updateSubscribedInfo]);

  useEffect(() => {
    checkedPriceIdRef.current = selectedPrice.goodsPriceId;
  }, [selectedPrice.goodsPriceId]);

  useEffect(() => {
    if (statePayMode !== payMode) {
      resetInitData();
    }
  }, [payMode, resetInitData, statePayMode]);

  useEffect(() => {
    // 30s 内无需要重复取数据
    if (updateTime && new Date().getTime() - updateTime < 30 * 1000) {
      return;
    }
    getPackageData();
  }, [getPackageData, updateTime]);

  useEffect(() => {
    // 已选中的值初始化
    if (
      initRef.current &&
      navigateType !== 'POP' &&
      checkedPriceIdRef.current
    ) {
      const _price = getDefaultSelectedPrice(curPackage.goodsPriceList);
      if (_price.goodsPriceId !== checkedPriceIdRef.current) {
        setSelectedPrice(_price);
      }
    }

    initRef.current = false;
  }, [
    curPackage.goodsPriceList,
    getDefaultSelectedPrice,
    navigateType,
    setSelectedPrice,
  ]);

  useEffect(() => {
    return () => {
      Modal.close();
    };
  }, []);

  const _loading = useMemo(() => {
    if (updateTime && subscribedInfo.loaded === 1) {
      return 1;
    }
    if (subscribedInfo.loaded === 2 || loading === 2) {
      return 2;
    }
    return loading;
  }, [loading, subscribedInfo.loaded, updateTime]);

  return {
    state: {
      selectedPrice,
      curPackage,
      loading: _loading,
      checkedTerm,
      paying: payMode === EPayMode.ApplePay ? applePaying : paying,
      tab,
    },
    action: {
      onClickTerm,
      onChangeTerm,
      onClickSupplemental,
      onClickAppleAgreement,
      onClickPay,
      fetchPackageData,
      onSelectPrice,
      onChangeTab,
    },
  };
};

export default useDefaultPackage;
